import { supabase } from '../lib/supabase';
import type { Message } from '../types/chat';
import { detectContactInfo, resetDetectedLeads } from '../utils/contactInfoDetection';
import { generateUUID } from '../utils/uuid';

const CONVERSATION_TIMEOUT = 5 * 60 * 1000; // 5 minutes of inactivity = new conversation

interface ConversationSession {
  id: string;
  startTime: number;
  lastActivity: number;
  messageCount: number;
  chatbotPublicId: string; // No longer optional
}

let currentSession: ConversationSession | null = null;

export async function saveConversation(messages: Message[], chatbotPublicId: string): Promise<string | null> {
  try {
    // Filter out welcome bot messages
    const conversationMessages = messages.filter(msg => 
      !(msg.type === 'bot' && msg.id === messages[0].id)
    );

    // Check if there's at least one user message
    const hasUserMessage = conversationMessages.some(msg => msg.type === 'user');
    if (!hasUserMessage) return null;

    // Check if we need to create a new session
    const now = Date.now();
    if (!currentSession || (now - currentSession.lastActivity) > CONVERSATION_TIMEOUT) {
      currentSession = {
        id: generateUUID(),
        startTime: now,
        lastActivity: now,
        messageCount: 0,
        chatbotPublicId
      };
      // Reset lead detection for the new session
      resetDetectedLeads();
    }

    // Update the session
    currentSession.lastActivity = now;
    currentSession.messageCount = conversationMessages.length;

    // Find the first user message
    const firstUserMessage = conversationMessages.find(msg => msg.type === 'user');
    if (!firstUserMessage) return null;

    // Create or update the conversation
    const { data: conversation, error: conversationError } = await supabase
      .from('conversations')
      .upsert({
        id: currentSession.id,
        chatbot_public_id: chatbotPublicId,
        initial_message_content: firstUserMessage.content,
        message_count: currentSession.messageCount,
        duration_seconds: Math.round((now - currentSession.startTime) / 1000)
      })
      .select()
      .single();

    if (conversationError) throw conversationError;

    // Use upsert for messages instead of delete+insert
    const { error: messagesError } = await supabase
      .from('conversation_messages')
      .upsert(
        conversationMessages.map((msg, index) => ({
          message_id: msg.id, // Primary key for upsert
          conversation_id: conversation.id,
          role: msg.type,
          content: msg.content,
          created_at: msg.timestamp,
          sequence: index + 1
        })),
        { onConflict: 'message_id' } // Use message_id as conflict column
      );

    if (messagesError) throw messagesError;

    // Detect leads in user messages
    const leads = conversationMessages
      .filter(msg => msg.type === 'user')
      .flatMap(msg => detectContactInfo(msg.content, msg.id))
      .filter(info => info.value.trim());

    if (leads.length > 0) {
      try {
        // Use upsert to automatically handle duplicates
        const { data: leadsData, error: leadsError } = await supabase
          .from('conversation_contact_info')
          .upsert(
            leads.map(lead => ({
              conversation_id: conversation.id,
              chatbot_public_id: chatbotPublicId,
              type: lead.type,
              value: lead.value.trim(),
              message_id: lead.messageId,
              processed: false,
              updated_at: new Date().toISOString()
            })),
            { onConflict: 'conversation_id,type,value' }
          )
          .select();

        if (leadsError) {
          console.error('Error upserting leads:', leadsError);
        } else if (leadsData) {
          // No need to send email notifications here - the trigger will handle it
          // The send_lead_notification_trigger will create the email notifications
        }
      } catch (error) {
        console.error('Error saving leads:', error);
        // Continue execution - don't fail the entire save due to lead errors
      }
    }

    return conversation.id;
  } catch (err) {
    console.error('Error saving conversation:', err);
    return null;
  }
}

export function endConversationSession() {
  currentSession = null;
  // Reset lead detection when the session ends
  resetDetectedLeads();
}